import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import '../../App.css'
import libraryOptions from '../../configs/library.json'
import methodOptions from '../../configs/method.json'
import AgGridWrapper from '../../components/common/AgGridWrapper'
import { similarityHeaders } from '../../configs/GridConfig'
import { apiConfig } from '../../api/ApiConfig'
import { API_BASE_URL } from '../../configs/Constants'
import { v4 as uuidv4 } from 'uuid'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { setAppData } from '../../redux/features/appDataSlice'
import Modal from '../../components/common/Modal'
import Notification from '../../components/common/Notification'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Divider from '@mui/material/Divider'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import { styled } from '@mui/material/styles'
import classes from './similarity.module.css'
import SpinnerButton from '../../components/common/SpinnerButton'
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Chip,
  Alert,
} from '@mui/material'
import { FieldGroupWrapper } from '../../style/StyledElement'
import { Code } from '@mui/icons-material'

// Constants moved outside component
const SEARCH_STATES = {
  LOADING: 'LOADING',
  COMPLETE: 'COMPLETE',
  ERROR: 'ERROR',
  INVALID: 'INVALID',
}

const SMILES_MAX_LENGTH = 120
const SEARCH_OPTIONS = {
  SMILES: 'smiles',
  UNIPROT: 'uniprot',
}

const STORAGE_KEYS = {
  SMILES: 'smiles',
  UNIPROT: 'uniprot',
  LIBRARY: 'library',
  METHOD: 'method',
  SEARCH_RESULT: 'searchSimilarityResult',
  TARGET_PRED_SMILES: 'targetPredSmiles',
  SEARCH_TYPE: 'searchType',
}

// Styled components for professional appearance
const HeaderContainer = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
  color: '#FFFFFF',
  padding: theme.spacing(3, 4),
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
}))

const ContentContainer = styled(Paper)(({ theme }) => ({
  background: '#FFFFFF',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  border: '1px solid #E5E7EB',
  marginBottom: theme.spacing(2),
}))

const SearchSection = styled(Card)(({ theme }) => ({
  background: '#F9FAFB',
  border: '1px solid #E5E7EB',
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
}))

const ResultsSection = styled(Box)(({ theme }) => ({
  background: '#FFFFFF',
  border: '1px solid #E5E7EB',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
}))

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#FFFFFF',
    '& fieldset': {
      borderColor: '#D1D5DB',
    },
    '&:hover fieldset': {
      borderColor: '#6B7280',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#4B5563',
    }
  },
  '& .MuiInputLabel-root': {
    color: '#6B7280',
    '&.Mui-focused': {
      color: '#4B5563',
    }
  }
}))

const StyledFormControl = styled(FormControl)(() => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#FFFFFF',
    '& fieldset': {
      borderColor: '#D1D5DB',
    },
    '&:hover fieldset': {
      borderColor: '#6B7280',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#4B5563',
    }
  },
  '& .MuiInputLabel-root': {
    color: '#6B7280',
    '&.Mui-focused': {
      color: '#4B5563',
    }
  }
}))

const FeatureChip = styled(Chip)(() => ({
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: '#FFFFFF',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  }
}))

const StyledRadioGroup = styled(RadioGroup)(() => ({
  '& .MuiFormControlLabel-root': {
    '& .MuiRadio-root': {
      color: '#6B7280',
      '&.Mui-checked': {
        color: '#4B5563',
      }
    }
  }
}))

export const Similarity = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { getSimilarity } = apiConfig()
  const { userId } = useSelector((state) => state.auth)

  // State management
  const [searchState, setSearchState] = useState(null)
  const [smilesResults, setSmilesResults] = useState([]);
  const [uniprotResults, setUniprotResults] = useState([]);
  const [smiles, setSmiles] = useState('')
  const [uniprot, setUniprot] = useState('')
  const [library, setLibrary] = useState(null)
  const [method, setMethod] = useState(null)
  const [selectedOption, setSelectedOption] = useState(SEARCH_OPTIONS.SMILES)
  const [notification, setNotification] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [download, setDownload] = useState(false)
  const [requestId, setRequestId] = useState('')
  const [isTriggeringPipeline, setIsTriggeringPipeline] = useState(false)
  const [similarityLogs, setSimilarityLogs] = useState([])
  const eventSourceRef = useRef(null)

  const startStreaming = useCallback((reqId) => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    const url = `${API_BASE_URL}/data/stream/similarity/logs/${reqId}`
    console.log('Starting stream:', url)

    const eventSource = new EventSource(url)
    eventSourceRef.current = eventSource

    eventSource.onmessage = (event) => {
      try {
        console.log('Stream message received:', event.data)
        const data = JSON.parse(event.data)
        if (data.error) {
          console.error('Stream error:', data.error)
          eventSource.close()
          return
        }
        const newLogs = Object.values(data)
        if (newLogs.length > 0) {
          console.log('Updating logs with:', newLogs)
          setSimilarityLogs((prev) => [...prev, ...newLogs])
        }
      } catch (e) {
        console.error('Error parsing stream data:', e)
      }
    }

    eventSource.onerror = (err) => {
      console.error('EventSource failed:', err)
      eventSource.close()
    }
  }, [])

  const stopStreaming = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }
    }
  }, [])

  // Utility functions
  const setLocalStorageItem = useCallback((key, value) => {
    try {
      const storageValue = typeof value === 'object' ? JSON.stringify(value) : value
      localStorage.setItem(key, storageValue)
    } catch (error) {
      console.error('Error setting localStorage:', error)
    }
  }, [])

  const getLocalStorageItem = useCallback((key) => {
    try {
      const item = localStorage.getItem(key)
      return item ? JSON.parse(item) : null
    } catch (error) {
      console.error(`Error parsing JSON from localStorage: ${error}`)
      return localStorage.getItem(key) || null
    }
  }, [])

  // Initialize state from localStorage
  useEffect(() => {
    const storedSmiles = localStorage.getItem(STORAGE_KEYS.SMILES) || ''
    const storedUniprot = localStorage.getItem(STORAGE_KEYS.UNIPROT) || ''
    const storedLibrary = getLocalStorageItem(STORAGE_KEYS.LIBRARY)
    const storedMethod = getLocalStorageItem(STORAGE_KEYS.METHOD)

    setSmiles(storedSmiles)
    setUniprot(storedUniprot)
    setLibrary(storedLibrary)
    setMethod(storedMethod)
  }, [getLocalStorageItem])


    // Initialize search results from localStorage
    useEffect(() => {
      const storedSmilesResults = localStorage.getItem('smilesResults');
      const storedUniprotResults = localStorage.getItem('uniprotResults');
      if (storedSmilesResults) {
        try {
          const parsedSmilesResults = JSON.parse(storedSmilesResults);
          if (Array.isArray(parsedSmilesResults)) {
            setSmilesResults(parsedSmilesResults);
          }
        } catch (error) {
          console.error('Error parsing stored smiles results:', error);
        }
      }
      if (storedUniprotResults) {
        try {
          const parsedUniprotResults = JSON.parse(storedUniprotResults);
          if (Array.isArray(parsedUniprotResults)) {
            setUniprotResults(parsedUniprotResults);
          }
        } catch (error) {
          console.error('Error parsing stored uniprot results:', error);
        }
      }
    }, []);     
    
  

  // Persist state to localStorage
  useEffect(() => {
    if (smiles) setLocalStorageItem(STORAGE_KEYS.SMILES, smiles)
  }, [smiles, setLocalStorageItem])

  useEffect(() => {
    if (uniprot) setLocalStorageItem(STORAGE_KEYS.UNIPROT, uniprot)
  }, [uniprot, setLocalStorageItem])

  useEffect(() => {
    if (library) setLocalStorageItem(STORAGE_KEYS.LIBRARY, library)
  }, [library, setLocalStorageItem])

  useEffect(() => {
    if (method) setLocalStorageItem(STORAGE_KEYS.METHOD, method)
  }, [method, setLocalStorageItem])

  // Validation logic
  useEffect(() => {
    if (selectedOption === SEARCH_OPTIONS.SMILES && smiles.length > SMILES_MAX_LENGTH) {
      setSearchState(SEARCH_STATES.INVALID)
    } else if (searchState === SEARCH_STATES.INVALID) {
      setSearchState(null)
    }
  }, [smiles, selectedOption, searchState])

  // Event handlers
const handleRadioChange = useCallback((value) => {
  setSelectedOption(value);
  localStorage.setItem(STORAGE_KEYS.SEARCH_TYPE, value); // Save selection

  const storedLibrary = getLocalStorageItem(STORAGE_KEYS.LIBRARY);
  const storedMethod = getLocalStorageItem(STORAGE_KEYS.METHOD);
  

  if (storedLibrary && storedMethod) {
    setLibrary(storedLibrary);
    setMethod(storedMethod);
  } else if (value === SEARCH_OPTIONS.UNIPROT) {
    setLibrary('HMDB');
    setMethod('Combined');
  } else {
    setLibrary(null);
    setMethod(null);
    setSearchState(null);
  }
}, [getLocalStorageItem]);

  const clearAll = useCallback(() => {
    try {
      localStorage.clear()
      setSmiles('')
      setUniprot('')
      setLibrary(null)
      setMethod(null)
      setSmilesResults([]);
      setUniprotResults([]);
      setSearchState(null)
    } catch (error) {
      console.error('Error clearing data:', error)
    }
  }, [])

  const handleClearButtonClick = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const handleModalConfirm = useCallback(() => {
    clearAll()
    setIsModalOpen(false)
    setNotification(true)
  }, [clearAll])

  const handleDownload = useCallback(() => {
    setDownload(true)
  }, [])

  const onRowClick = useCallback((data) => {
    if (data?.smiles) {
      localStorage.setItem(STORAGE_KEYS.TARGET_PRED_SMILES, data.smiles)
      navigate('/target-prediction/')
    }
  }, [navigate])

  // Search function
  const search = useCallback(async () => {
    if (searchState === SEARCH_STATES.INVALID) {
      return false
    }

    // Validation
    const currentInput = selectedOption === SEARCH_OPTIONS.SMILES ? smiles : uniprot
    if (!currentInput?.trim()) {
      return false
    }

    if (selectedOption === SEARCH_OPTIONS.SMILES && (!library || !method)) {
      return false
    }

    setSearchState(SEARCH_STATES.LOADING)
    localStorage.setItem(STORAGE_KEYS.SEARCH_RESULT, 'InProgress')
    setSimilarityLogs([])

    const reqId = uuidv4()
    startStreaming(reqId)

    try {
      const formData = new FormData()
      // Only send the relevant input
      if (selectedOption === SEARCH_OPTIONS.SMILES) {
        formData.append('smiles', smiles)
      } else {
        formData.append('uniprot', uniprot)
      }

      formData.append('library', library || '')
      formData.append('method', method || '')
      formData.append('userid', userId || '')
      formData.append('request_id', reqId)

      const formDataAsObj = Object.fromEntries(formData.entries())
      console.log('Search Data:', formDataAsObj)

      dispatch(setAppData(formDataAsObj))

      const data = await getSimilarity(JSON.stringify(formDataAsObj))

      
      if (data && Array.isArray(data)) {
        const firstResult=data[0];
        const resultRequestId=firstResult.requestId || '';
        setRequestId(resultRequestId);
        if (selectedOption === SEARCH_OPTIONS.SMILES) {
          setSmilesResults(data);
          localStorage.setItem('smilesResults', JSON.stringify(data));
        } else {
          setUniprotResults(data);
          localStorage.setItem('uniprotResults', JSON.stringify(data));
        }
        setSearchState(SEARCH_STATES.COMPLETE)
        return true
      } else {
        setSearchState(SEARCH_STATES.ERROR)
        localStorage.setItem(STORAGE_KEYS.SEARCH_RESULT, SEARCH_STATES.ERROR) 
        return false
      }
    } catch (error) {
      console.error('Search Error:', error)
      localStorage.setItem(STORAGE_KEYS.SEARCH_RESULT, SEARCH_STATES.ERROR)
      setSearchState(SEARCH_STATES.ERROR)
      return false
    } finally {
      stopStreaming()
    }
  }, [
    searchState,
    selectedOption,
    smiles,
    uniprot,
    library,
    method,
    userId,
    getSimilarity,
    dispatch,
    startStreaming,
    stopStreaming,
  ])
  // Pipeline trigger function( Moved above to access hasResults )
  const currentResults = selectedOption === SEARCH_OPTIONS.SMILES ? smilesResults : uniprotResults;
  const hasResults = currentResults.length > 0
  const triggerPipeline = useCallback(async () => {
  if (!hasResults || !requestId) return

  setIsTriggeringPipeline(true)
  try {
    const response = await fetch('/similarity/pipeline', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        results: currentResults,
        request_id: requestId,
      }),
    })

    if (response.ok) {
      setNotification('Pipeline triggered successfully')
    } else {
      setNotification('Pipeline trigger failed')
    }
  } catch (error) {
    console.error('Trigger pipeline error:', error)
    setNotification('Pipeline trigger failed')
  } finally {
    setIsTriggeringPipeline(false)
  }
}, [hasResults, requestId, currentResults])

  // Computed values
  const isLoading = searchState === SEARCH_STATES.LOADING
  const hasError = searchState === SEARCH_STATES.ERROR
  const isInvalid = searchState === SEARCH_STATES.INVALID
  const currentInputValue = selectedOption === SEARCH_OPTIONS.SMILES ? smiles : uniprot
  
  const isSearchDisabled = useMemo(() => {
    if (isLoading || isInvalid) return true
    if (!currentInputValue?.trim()) return true
    if (selectedOption === SEARCH_OPTIONS.SMILES && (!library || !method)) return true
    return false
  }, [isLoading, isInvalid, currentInputValue, selectedOption, library, method])
  
  const smilesError = selectedOption === SEARCH_OPTIONS.SMILES && smiles.length > SMILES_MAX_LENGTH
  const smilesHelperText = smilesError ? (
    <>
      SMILES length should <br />
      be less than {SMILES_MAX_LENGTH}
    </>
  ) : ''

  return (
    <Box>
      {/* Professional Header */}
      <HeaderContainer elevation={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 600, 
                marginBottom: 1,
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              Molecular Similarity Search
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                opacity: 0.9,
                fontSize: '1.1rem',
                lineHeight: 1.5,
                marginBottom: 2
              }}
            >
              Find structurally similar compounds using advanced molecular fingerprinting methods
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <FeatureChip label="Tanimoto Similarity" size="small" />
              <FeatureChip label="Chemical Libraries" size="small" />
              <FeatureChip label="Fingerprint Methods" size="small" />
            </Box>
          </Box>
          <Box sx={{ 
            display: { xs: 'none', md: 'block' },
            opacity: 0.3,
            fontSize: '4rem',
            fontWeight: 300
          }}>
            🔍
          </Box>
        </Box>
      </HeaderContainer>

      {/* Main Content */}
      <ContentContainer elevation={0}>
        {/* Search Configuration Section */}
        <SearchSection elevation={0}>
          <CardContent>
            <Typography 
              variant="h6" 
              sx={{ 
                marginBottom: 2, 
                color: '#374151',
                fontWeight: 600
              }}
            >
              Search Configuration
            </Typography>
            <Divider sx={{ marginBottom: 3, backgroundColor: '#E5E7EB' }} />

            <div className={classes.fieldWrapper}>
              <FieldGroupWrapper>
                <Box sx={{ mb: 3 }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      marginBottom: 1, 
                      color: '#4B5563',
                      fontWeight: 500
                    }}
                  >
                    Search Type
                  </Typography>
                  <StyledRadioGroup defaultValue={selectedOption} value={selectedOption} row>
                    <FormControlLabel
                      value={SEARCH_OPTIONS.SMILES}
                      control={<Radio />}
                      label="SMILES Structure"
                      onChange={() => handleRadioChange(SEARCH_OPTIONS.SMILES)}
                    />
                    <FormControlLabel
                      value={SEARCH_OPTIONS.UNIPROT}
                      onChange={() => handleRadioChange(SEARCH_OPTIONS.UNIPROT)}
                      control={<Radio />}
                      label="UniProt ID"
                    />
                  </StyledRadioGroup>
                </Box>
              </FieldGroupWrapper>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  justifyContent: 'start',
                  gap: '1rem',
                }}
              >
                <StyledTextField
                  sx={{ 
                    mt: smilesError ? '40px' : '0',
                    minWidth: '300px',
                    flex: 1
                  }}
                  label={selectedOption === SEARCH_OPTIONS.SMILES ? 'SMILES Notation' : 'UniProt ID'}
                  placeholder={selectedOption === SEARCH_OPTIONS.SMILES ? 'Enter SMILES (e.g., CCO)' : 'Enter UniProt ID (e.g., P12345)'}
                  value={currentInputValue}
                  onChange={(e) => {
                    const value = e.target.value
                    if (selectedOption === SEARCH_OPTIONS.SMILES) {
                      setSmiles(value)
                    } else {
                      setUniprot(value)
                    }
                  }}
                  error={smilesError}
                  id={smilesError ? 'component-error' : undefined}
                  helperText={smilesHelperText}
                  disabled={isLoading}
                />

                {selectedOption === SEARCH_OPTIONS.SMILES && (
                  <>
                    <StyledFormControl className={classes.dropdownSize} sx={{ minWidth: '150px' }}>
                      <InputLabel id="libraryId">Chemical Library</InputLabel>
                      <Select
                        value={library || ''}
                        labelId="libraryId"
                        label="Chemical Library"
                        onChange={(e) => setLibrary(e.target.value)}
                        disabled={isLoading}
                      >
                        {libraryOptions.map((item, index) => (
                          <MenuItem key={`library-${index}`} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </StyledFormControl>

                    <StyledFormControl className={classes.dropdownSize} sx={{ minWidth: '150px' }}>
                      <InputLabel id="methodLabel">Similarity Method</InputLabel>
                      <Select
                        labelId="methodLabel"
                        label="Similarity Method"
                        value={method || ''}
                        onChange={(e) => setMethod(e.target.value)}
                        disabled={isLoading}
                      >
                        {methodOptions.map((item, index) => (
                          <MenuItem key={`method-${index}`} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </StyledFormControl>
                  </>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    flexWrap: 'wrap'
                  }}
                >
                  <SpinnerButton
                    label="Search Similarities"
                    className="btn-primary"
                    onClick={search}
                    height="38px"
                    disabled={isSearchDisabled}
                    loading={isLoading}
                    sx={{
                      background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #4B5563 0%, #374151 100%)',
                      },
                      fontWeight: 600,
                      textTransform: 'none',
                      minWidth: '140px'
                    }}
                  />
                  <SpinnerButton
                    label="Clear All"
                    buttonColor="white"
                    textColor="black"
                    onClick={handleClearButtonClick}
                    disabled={isLoading}
                    sx={{
                      border: '1px solid #D1D5DB',
                      '&:hover': {
                        backgroundColor: '#F9FAFB',
                        borderColor: '#6B7280'
                      }
                    }}
                  />
                  <SpinnerButton
                    label="Download"
                    buttonColor="white"
                    textColor="black"
                    onClick={handleDownload}
                    disabled={!currentResults.length || isLoading}
                    sx={{
                      border: '1px solid #D1D5DB',
                      '&:hover': {
                        backgroundColor: '#F9FAFB',
                        borderColor: '#6B7280'
                      }
                    }}
                  />
                  <SpinnerButton
                    label="Trigger Pipeline"
                    buttonColor="white"
                    textColor="black"
                    onClick={triggerPipeline}
                    disabled={!hasResults || isLoading || isTriggeringPipeline}
                    loading={isTriggeringPipeline}
                    sx={{
                      border: '1px solid #D1D5DB',
                      '&:hover': {
                        backgroundColor: '#F9FAFB',
                        borderColor: '#6B7280'
                      }
                    }}
                  />
                </Box>
              </Box>
            </div>
          </CardContent>
        </SearchSection>

        {/* Logs Section */}
        {(isLoading || similarityLogs.length > 0) && (
          <Box sx={{ marginBottom: 3 }}>
             <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: '#1e1e1e',
                color: '#e0e0e0',
                borderRadius: 2,
                fontFamily: 'monospace',
                maxHeight: '200px',
                overflowY: 'auto',
                fontSize: '0.875rem',
                border: '1px solid #333'
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#90caf9',
                  mb: 1,
                  borderBottom: '1px solid #333',
                  pb: 0.5
                }}
              >
                Processing Logs
              </Typography>
              {similarityLogs.map((log, index) => (
                <Box key={index} sx={{ py: 0.25 }}>
                  <span style={{ color: '#66bb6a', marginRight: '8px' }}>➜</span>
                  {log}
                </Box>
              ))}
              {isLoading && (
                <Box sx={{ py: 0.25, fontStyle: 'italic', opacity: 0.7 }}>
                  <span style={{ display: 'inline-block', animation: 'pulse 1.5s infinite' }}>...</span>
                </Box>
              )}
            </Paper>
          </Box>
        )}

        {/* Results Section */}
        <Box>
          <Typography 
            variant="h6" 
            sx={{ 
              marginBottom: 2, 
              color: '#374151',
              fontWeight: 600
            }}
          >
            Similarity Results
          </Typography>
          <Divider sx={{ marginBottom: 2, backgroundColor: '#E5E7EB' }} />

          {/* Results summary */}
          {hasResults && searchState === SEARCH_STATES.COMPLETE && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 2,
                backgroundColor: '#ECFDF5',
                borderColor: '#A7F3D0',
                color: '#065F46'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography>
                  Found <strong>{currentResults.length}</strong> similar compound{currentResults.length !== 1 ? 's' : ''} 
                  {selectedOption === SEARCH_OPTIONS.SMILES && library && method && (
                    <> using <strong>{method}</strong> method in <strong>{library}</strong> library</>
                  )}
                  {selectedOption === SEARCH_OPTIONS.UNIPROT && (
                    <> for UniProt ID <strong>{uniprot}</strong></>
                  )}
                </Typography>
              </Box>
            </Alert>
          )}

          {/* No results message */}
          {searchState === SEARCH_STATES.COMPLETE && !hasResults && (
            <Alert 
              severity="info" 
              sx={{ 
                mb: 2,
                backgroundColor: '#EFF6FF',
                borderColor: '#BFDBFE',
                color: '#1E40AF'
              }}
            >
              No similar compounds found. Try adjusting your search parameters or using a different molecular structure.
            </Alert>
          )}

          <ResultsSection>
            {hasResults &&
            searchState === SEARCH_STATES.COMPLETE && (
            <AgGridWrapper
              data={currentResults}
              headers={similarityHeaders}
              loading={isLoading}
              error={hasError}
              api={onRowClick}
              downloadStatus={download}
              setDownloadStatus={setDownload}
            />
            )}  
          </ResultsSection>
        </Box>
      </ContentContainer>

      {isModalOpen && (
        <Modal
          onClose={handleModalClose}
          onConfirm={handleModalConfirm}
          children="Confirm Clear?"
        />
      )}

      {notification && (
        <Notification
          type="success"
          onClose={() => setNotification(null)}
          children="Cleared Successfully"
        />
      )}
    </Box>
  )
}
